import React from "react"
import dayjs from "dayjs"
import 'dayjs/locale/ja'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { Clock, Check, X } from "lucide-react"
import { ChangedSchedule, ScheduleShiftItem } from "@/types/cast_api_def"
import { formatBusinessTime, BUSINESS_DAY_OFFSET_HOURS } from "@new-cti/frontend-shared/time/business-day"

// 日本語ロケールを設定
dayjs.locale('ja')

// 日付変更の境界（5時）。値の正典は共有パッケージ側にある（さらに大元はバックエンドの
// commonConstants.BusinessDayOffsetHours / CTI の DATE_LINE）。ISSUE#135 で各アプリの手書きを廃した。
export const DAY_THRESHOLD_HOUR = BUSINESS_DAY_OFFSET_HOURS

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// 1日の日付区切り時間を指定した年月日を取得
export function dayWithThreshold(isoDatetime : string | null = null, thresholdHour : number = DAY_THRESHOLD_HOUR, format : string = "YYYY-MM-DD") {
  //isoDatetimeの23:59:59を越えても、thresholdHourを過ぎるまでは前日扱いにする
  const dt = isoDatetime ? dayjs(isoDatetime) : dayjs()
  if (dt.hour() < thresholdHour) {
    return dt.subtract(1, 'day').format(format)
  } else {
    return dt.format(format)
  }
}

// 日付が初期値(0001-01-01)かどうかを判定 
export function isInitialDate(isoDatetime: string | undefined) {
  if(!isoDatetime) return true
  const dayjsObj = dayjs(isoDatetime)
  if (!dayjsObj.isValid()) return true
  return dayjsObj.year() === 1
}

//任意の書式で日付文字列を取得
export function getCustomFormattedDate(isoDatetime: string | undefined, format: string) {
  if (isInitialDate(isoDatetime)) return ""
  return dayjs(isoDatetime).format(format)
}

//YYYY-MM-DD　形式の日付文字列を取得
export function getDayStr(isoDatetime: string | undefined) {
  if(!isoDatetime) return ""
  return getCustomFormattedDate(isoDatetime, "YYYY-MM-DD")
}

// 通知一覧の日時表示（年月日時分・秒なし）。#211
// 🔴 区切り・桁はドライバーアプリの通知表記に合わせている
//    （driver/app/components/notification-panel.tsx の formatTimestamp が
//     toLocaleDateString("ja-JP",{year:"numeric",month:"2-digit",day:"2-digit"}) + " " + HH:MM
//     ＝「2026/07/28 10:30」を出すため、こちらも "YYYY/MM/DD HH:mm" にする）。
export function getDayTimeStr(isoDatetime: string | undefined) {
  if (!isoDatetime) return ""
  return getCustomFormattedDate(isoDatetime, "YYYY/MM/DD HH:mm")
}

// 今日の日付ならHH:MM、それ以外はYYYY-MM-DD形式で返す
export function getDayOrTimeStr(isoDatetime: string | undefined) {
  if (!isoDatetime) return ""
  const now = dayjs()
  const target = dayjs(isoDatetime)
  if (now.isSame(target, 'day')) {
    return getHHMMStr(isoDatetime)
  }
  return getDayStr(isoDatetime)
}

//ISO時刻からHH:MM部分だけを抽出（営業日表記。深夜は24時以降で表す）
// ISSUE#135: 0〜4時台を暦日のまま出すと、シフトやオーダーが CTI と別の時刻に見える。
// 表記の実体は共有パッケージ(@new-cti/frontend-shared/time/business-day)に集約している。
export function getHHMMStr(isoDatetime: string | undefined) {
  if(!isoDatetime) return ""
  return formatBusinessTime(getCustomFormattedDate(isoDatetime, "HH:mm"))
}

//YYYY年M月D日（曜日）形式の日付文字列を取得
export function getJapaneseDayStr(isoDatetime: string | undefined, withDayOfWeek: boolean = false) {
  if(!isoDatetime) return ""
  return getCustomFormattedDate(isoDatetime, 'YYYY年M月D日' + (withDayOfWeek ? '(ddd)' : ''))
}

// 分を時間:分形式に変換
export function formatTime(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}時間${minutes}分`
}

// 前月実績が無い（API が null を返した）ときの共通表示。
// 🔴 ±0% / ±0日 や +100% と描画してはいけない（「実績なし」と「増減なし」を区別する）。
//    表示側は括弧で囲むため、ここはダッシュのみ（実績なし=「(—)」／増減なし=「(±0%)」で区別される）。
const NO_PREV_MONTH_TEXT = "—"

const formatMonthOverMonthWithUnit = (value: number | null | undefined, unit: string) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return NO_PREV_MONTH_TEXT
  }
  return `${formatDiffNumber(value)}${unit}`
}

// 前月比（増減率 %）の表示用フォーマット。配分・接客数・本指名で使う。
export const formatMonthOverMonth = (value: number | null | undefined) =>
  formatMonthOverMonthWithUnit(value, "%")

// 出勤日数だけは前月「差」（日数）で見せる（#151-1 F・gabe 決定）。
// API も率ではなく差（今月 − 前月）を返す。例: +2日 / -1日 / ±0日
export const formatMonthOverMonthDays = (value: number | null | undefined) =>
  formatMonthOverMonthWithUnit(value, "日")

// 前月比の数値をフォーマットする共通関数
export const formatDiffNumber = (value: number) => {
  if (value > 0) {
    return `+${value}`
  }
  if (value === 0) {
    return `±0`
  }
  return value // 負の値はそのまま
}

// 本指名率（指名あり ÷ 完了）の表示用。母数0は「—」。#117
// 店の本指名率(nominatedCount/totalUseCount)・私への本指名率(nominateCount/attendCount)の両方で使う。
export const formatNominateRate = (nominated: number, total: number) =>
  total > 0 ? `${Math.round((nominated / total) * 100)}%` : "—"

// 顧客生涯系の日付（CTI・YYYY-MM-DD）の表示用。空なら「—」。#117
// 接客履歴の日付（getDayStr＝YYYY-MM-DD）と平仄を合わせて ISO 表記のまま出す。
export const formatUseDate = (date: string | undefined) =>
  date || "—"

// 金額を ¥ 表記にする。負値は -¥1,234（¥-1,234 ではない）。#174
// 符号の出し分けはここ 1 箇所に集約する（各所で ¥ と符号を手で組まない）。
export const formatYen = (value: number) =>
  value < 0 ? `-¥${Math.abs(value).toLocaleString()}` : `¥${value.toLocaleString()}`

// ステータスに応じた色を返す関数
export const getStatusColor = (status: string) => {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-800"
    case "in_progress":
      return "bg-blue-100 text-blue-800"
    case "pending":
      return "bg-yellow-100 text-yellow-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

// ステータスに応じた表示テキストを返す関数
export const getStatusText = (status: string) => {
  switch (status) {
    case "completed":
      return "対応完了"
    case "in_progress":
      return "対応中"
    case "pending":
      return "受付中"
    default:
      return "不明"
  }
}

// シフトステータスに応じた表示テキストを返す関数
export const getShiftStatusText = (record: ScheduleShiftItem | ChangedSchedule) => {
  const status = getShiftStatusString(record)
  switch (status) {
    case "approved":
      return "承認済み"
    case "pending":
      return "承認待ち"
    case "rejected":
      return "却下"
    default:
      return ""
  }
}

// 顧客ステータス
export const getCustomerStatusLabel = (statusId: number) => {
  switch (statusId) {
    case 1:
      return "会員"
    case 2:
      return "G新規"
    case 3:
      return "ネット新規"
    default:
      return "不明"
  }
}

const getShiftStatusString = (record: ScheduleShiftItem | ChangedSchedule) => {
  if('approved' in record){
    if('declined' in record && record.declined){
      return "rejected"
    }
    if(record.approved){
      return "approved"
    } else {
      return "pending"
    }
  }
  if('approvedAt' in record && record.approvedAt){
    return "approved"
  }
  if('declinedAt' in record && record.declinedAt){
    return "rejected"
  }
  return "unknown"
}

// シフトステータスの色を返す関数
export const getShiftStatusColor = (record: ScheduleShiftItem | ChangedSchedule) => {
  const status = getShiftStatusString(record)
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-800 border-green-200"
    case "pending":
      return "bg-yellow-100 text-yellow-800 border-yellow-200"
    case "rejected":
      return "bg-red-100 text-red-800 border-red-200"
    default:
      return "bg-gray-100 text-gray-800 border-gray-200"
  }
}

// シフトステータスに応じたアイコンを返す関数
export const getShiftStatusIcon = (record: ScheduleShiftItem | ChangedSchedule) => {
  const status = getShiftStatusString(record)
  switch (status) {
    case "approved":
      return React.createElement(Check, { className: "h-3 w-3" })
    case "pending":
      return React.createElement(Clock, { className: "h-3 w-3" })
    case "rejected":
      return React.createElement(X, { className: "h-3 w-3" })
    default:
      return null
  }
}


